"use client";
import React, { useState, useRef } from "react";
import Link from "next/link";
import { HoveredLink } from "@/components/ui/navbar-menu";
import { motion, AnimatePresence } from "framer-motion";
import { Tooltip } from "@/components/ui/tooltip";
import {
  Bot,
  Building2,
  Cpu,
  DollarSign,
  Info,
  Mail,
  PenLine,
  ShoppingBag,
  Users,
} from "lucide-react";

type NavItem = {
  key: string;
  label: string;
  href?: string;
  tooltip: string;
  type?: "customersMenu";
  enabled?: boolean;
  icon: React.ComponentType<{ className?: string }>;
};

type CustomerMenuItem = {
  key: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  tooltip: string;
  enabled?: boolean;
};

const customerMenuItems: CustomerMenuItem[] = [
  {
    key: "retail-commerce",
    label: "Retail & Commerce",
    href: "/customers/commerce",
    icon: ShoppingBag,
    tooltip: "Retail & commerce use cases",
  },
];

const desktopNavItems: NavItem[] = [
  {
    key: "customers",
    label: "Customers",
    tooltip: "Customer stories and use cases",
    type: "customersMenu",
    enabled: false,
    icon: Users,
  },
  {
    key: "about",
    label: "About",
    href: "/about-us",
    tooltip: "Our story and team",
    enabled: false,
    icon: Info,
  },
  {
    key: "enterprise",
    label: "Enterprise",
    href: "/enterprise-solutions",
    tooltip: "Enterprise solutions",
    enabled: false,
    icon: Building2,
  },
  {
    key: "self-serve",
    label: "Self-serve",
    href: "/self-serve",
    tooltip: "ratl.ai Studio: AI-agentic testing platform",
    icon: Cpu,
  },
  {
    key: "managed",
    label: "Managed",
    href: "/managed",
    tooltip: "Expert-led QA using AI-agentic workflows",
    icon: Bot,
  },
  {
    key: "pricing",
    label: "Pricing",
    href: "/pricing",
    tooltip: "Start free. Upgrade when you need more",
    icon: DollarSign,
  },
  {
    key: "blog",
    label: "Blog",
    href: "/blog",
    tooltip: "Latest updates and articles",
    icon: PenLine,
  },
];

const mobileNavItems: NavItem[] = [
  {
    key: "customers",
    label: "Customers",
    href: "/customers",
    tooltip: "Customer stories and use cases",
    enabled: false,
    icon: Users,
  },
  {
    key: "about",
    label: "About",
    href: "/about-us",
    tooltip: "Our story and team",
    enabled: false,
    icon: Info,
  },
  {
    key: "enterprise",
    label: "Enterprise",
    href: "/enterprise-solutions",
    tooltip: "Enterprise solutions",
    enabled: false,
    icon: Building2,
  },
  {
    key: "self-serve",
    label: "Self-serve",
    href: "/self-serve",
    tooltip: "ratl.ai Studio: AI-agentic testing platform",
    icon: Cpu,
  },
  {
    key: "managed",
    label: "Managed",
    href: "/managed",
    tooltip: "Expert-led QA using AI-agentic workflows",
    icon: Bot,
  },
  {
    key: "pricing",
    label: "Pricing",
    href: "/pricing",
    tooltip: "Start free. Upgrade when you need more",
    icon: DollarSign,
  },
  {
    key: "blog",
    label: "Blog",
    href: "/blog",
    tooltip: "Latest updates and articles",
    icon: PenLine,
  },
  {
    key: "newsletter",
    label: "Join Newsletter",
    href: "/newsletter",
    tooltip: "Get product updates",
    icon: Mail,
  },
];

const NavbarLinks: React.FC = () => {
  const [active, setActive] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActive(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActive(null);
    }, 100);
  };

  const handleNavClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActive(null);
    if (event?.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
  };

  const transition = {
    type: "spring",
    mass: 0.5,
    damping: 11.5,
    stiffness: 100,
    restDelta: 0.001,
    restSpeed: 0.001,
  };

  return (
    <nav>
      <ul className="flex flex-col lg:flex-row lg:items-center font-medium gap-6 lg:gap-1 lg:px-7">
        {desktopNavItems
          .filter((item) => item.enabled !== false)
          .map((item) => {
          const isEnabled = item.enabled !== false;
          if (item.type === "customersMenu") {
            return (
              <li key={item.key} className="hidden lg:block lg:px-3">
                <div className="relative">
                  {isEnabled ? (
                    <Tooltip content={item.tooltip}>
                      <Link
                        href="/customers"
                        onMouseEnter={() => handleMouseEnter(item.label)}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleNavClick}
                        className="text-sm text-light-body-text hover:text-black cursor-pointer flex items-center gap-1"
                      >
                        <item.icon className="h-4 w-4 opacity-70" />
                        {item.label}
                      </Link>
                    </Tooltip>
                  ) : (
                    <span
                      aria-disabled="true"
                      className="text-sm text-light-body-text/50 cursor-not-allowed"
                    >
                      {item.label}
                    </span>
                  )}
                  <AnimatePresence>
                    {isEnabled && active === item.label && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.85, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.85, y: 10 }}
                        transition={transition}
                        className="absolute top-[calc(100%_+_1.2rem)] -left-14 z-50"
                        onMouseEnter={() => handleMouseEnter(item.label)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <motion.div
                          transition={transition}
                          layoutId="active"
                          className="rounded-lg border border-[#F2F1F0] bg-white/95 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.10)] backdrop-blur-[1px] overflow-hidden"
                        >
                          <motion.div layout className="w-max h-full p-2">
                            <div className="flex flex-col gap-2 text-sm">
                              {customerMenuItems.map((menuItem) => {
                                const isMenuEnabled = menuItem.enabled !== false;
                                const MenuIcon = menuItem.icon;
                                return isMenuEnabled ? (
                                  <Tooltip
                                    key={menuItem.key}
                                    content={menuItem.tooltip}
                                    className="block"
                                  >
                                    <HoveredLink
                                      href={menuItem.href}
                                      onClick={handleNavClick}
                                    >
                                      <div className="flex items-center gap-1 p-2">
                                        <MenuIcon className="h-4 w-4 opacity-70" />
                                        <span>{menuItem.label}</span>
                                      </div>
                                    </HoveredLink>
                                  </Tooltip>
                                ) : (
                                  <div
                                    key={menuItem.key}
                                    aria-disabled="true"
                                    className="flex items-center gap-1 p-2 text-light-body-text/50 cursor-not-allowed"
                                  >
                                    <MenuIcon className="h-4 w-4 opacity-40" />
                                    <span>{menuItem.label}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </li>
            );
          }
          return (
            <li key={item.key} className="hidden lg:block lg:px-3">
              <Tooltip content={item.tooltip}>
                <Link
                  href={item.href ?? "#"}
                  onClick={handleNavClick}
                  className="text-sm whitespace-nowrap flex items-center gap-1 p-1 text-light-body-text hover:text-black"
                >
                  <item.icon className="h-4 w-4 opacity-70" />
                  {item.label}
                </Link>
              </Tooltip>
            </li>
          );
        })}

        {mobileNavItems
          .filter((item) => item.enabled !== false)
          .map((item) => {
          return (
            <li key={item.key} className="lg:hidden lg:px-3">
              <Link
                href={item.href ?? "#"}
                className="text-sm whitespace-nowrap flex items-center gap-1 p-1 text-light-body-text hover:text-black"
              >
                <item.icon className="h-4 w-4 opacity-70" />
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default NavbarLinks;
